import {
  Room,
  RoomEvent,
  Track,
} from "livekit-client";

const TARGET_SAMPLE_RATE = 16000;

/**
 * Initialise a LiveKit connection with callback-based state notification.
 *
 * @param {Object} opts
 * @param {string} opts.url        - LiveKit server URL
 * @param {string} opts.token      - LiveKit access token
 * @param {AudioContext} [opts.audioCtx] - External AudioContext (created during user gesture)
 * @param {Function} opts.onStateChange - Called whenever any observable state changes
 * @returns {{ getState, toggleMic, pauseCall, resumeCall, disconnectRoom }}
 */
export function initLiveKit({ url, token, audioCtx: externalAudioCtx, onStateChange, initialMessage }) {
  // --- State ---
  let agentVideoTrack = null;
  let agentAudioTrack = null;
  let currentSlide = 0;
  let currentSlideMediaType = null;
  let currentSlideMediaUrl = null;
  let isMicOn = false;
  let transcriptMessages = [];
  let agentStatus = "idle";
  let isPaused = false;
  let suggestions = [];
  let initialMessageEchoPending = !!initialMessage;

  let room = null;
  let audioCtx = null;
  let processor = null;
  let sourceNode = null;
  let resumeInterval = null;
  let isMicOnLocal = false;

  function notifyChange() {
    if (onStateChange) {
      onStateChange(getState());
    }
  }

  function getState() {
    return {
      agentVideoTrack,
      agentAudioTrack,
      currentSlide,
      currentSlideMediaType,
      currentSlideMediaUrl,
      isMicOn,
      transcriptMessages,
      agentStatus,
      isPaused,
      suggestions,
    };
  }

  // --- Audio capture helpers ---

  function getMicMediaStreamTrack(r) {
    const micPub = r.localParticipant.getTrackPublication(Track.Source.Microphone);
    return micPub?.track?.mediaStreamTrack ?? null;
  }

  async function waitForMicTrack(r, timeoutMs = 5000) {
    const immediate = getMicMediaStreamTrack(r);
    if (immediate) return immediate;

    return new Promise((resolve) => {
      const interval = 100;
      let elapsed = 0;
      const timer = setInterval(() => {
        elapsed += interval;
        const t = getMicMediaStreamTrack(r);
        if (t) {
          clearInterval(timer);
          resolve(t);
        } else if (elapsed >= timeoutMs) {
          clearInterval(timer);
          console.error("Timed out waiting for mic MediaStreamTrack");
          resolve(null);
        }
      }, interval);
    });
  }

  async function setupAudioCapture(r) {
    try {
      const mediaStreamTrack = await waitForMicTrack(r);
      if (!mediaStreamTrack) {
        console.warn("No mic MediaStreamTrack for data channel audio — audio capture disabled");
        return;
      }

      audioCtx = externalAudioCtx || new AudioContext();
      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }
      const nativeSampleRate = audioCtx.sampleRate;
      const ratio = nativeSampleRate / TARGET_SAMPLE_RATE;

      const stream = new MediaStream([mediaStreamTrack]);
      const source = audioCtx.createMediaStreamSource(stream);

      processor = audioCtx.createScriptProcessor(4096, 1, 1);

      const BATCH_COUNT = 4;
      let pendingChunks = [];
      let sending = false;

      processor.onaudioprocess = (e) => {
        if (!isMicOnLocal) return;

        const input = e.inputBuffer.getChannelData(0);
        const outputLen = Math.floor(input.length / ratio);
        const int16 = new Int16Array(outputLen);
        for (let i = 0; i < outputLen; i++) {
          const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        pendingChunks.push(new Uint8Array(int16.buffer));

        if (pendingChunks.length >= BATCH_COUNT && !sending) {
          const totalLen = pendingChunks.reduce((s, c) => s + c.length, 0);
          const merged = new Uint8Array(totalLen);
          let offset = 0;
          for (const chunk of pendingChunks) {
            merged.set(chunk, offset);
            offset += chunk.length;
          }
          pendingChunks = [];
          sending = true;
          r.localParticipant
            .publishData(merged, { reliable: true, topic: "mic-audio" })
            .catch(() => {})
            .finally(() => { sending = false; });
        }
      };

      sourceNode = source;
      sourceNode.connect(processor);
      const silentGain = audioCtx.createGain();
      silentGain.gain.value = 0;
      processor.connect(silentGain);
      silentGain.connect(audioCtx.destination);

      audioCtx.onstatechange = () => {
        console.log(`AudioContext state changed: ${audioCtx.state}`);
        if (audioCtx.state === "suspended") {
          audioCtx.resume().catch(() => {});
        }
      };

      resumeInterval = setInterval(() => {
        if (audioCtx.state === "suspended") {
          console.warn("AudioContext suspended, resuming...");
          audioCtx.resume().catch(() => {});
        }
      }, 500);

      console.log(
        `Audio capture: ${nativeSampleRate}Hz → ${TARGET_SAMPLE_RATE}Hz via data channel (state: ${audioCtx.state})`,
      );
    } catch (err) {
      console.error("Failed to setup audio capture:", err);
    }
  }

  async function reconnectAudioSource(r) {
    if (!audioCtx || !processor) return;
    const mediaStreamTrack = await waitForMicTrack(r);
    if (!mediaStreamTrack) return;
    if (sourceNode) {
      try { sourceNode.disconnect(); } catch {}
    }
    const stream = new MediaStream([mediaStreamTrack]);
    sourceNode = audioCtx.createMediaStreamSource(stream);
    sourceNode.connect(processor);
  }

  function cleanupAudioCapture() {
    if (resumeInterval) {
      clearInterval(resumeInterval);
      resumeInterval = null;
    }
    if (processor) {
      processor.disconnect();
      processor = null;
    }
    if (audioCtx) {
      if (!externalAudioCtx) {
        audioCtx.close();
      }
      audioCtx = null;
    }
  }

  // --- Room connection ---

  function connect(urlVal, tokenVal) {
    if (!urlVal || !tokenVal) return;

    room = new Room();

    const handleTrackSubscribed = (track) => {
      if (track.kind === Track.Kind.Video) {
        agentVideoTrack = track;
      } else if (track.kind === Track.Kind.Audio) {
        agentAudioTrack = track;
      }
      notifyChange();
    };

    const handleTrackUnsubscribed = (track) => {
      if (track.kind === Track.Kind.Video) {
        if (agentVideoTrack === track) agentVideoTrack = null;
      } else if (track.kind === Track.Kind.Audio) {
        if (agentAudioTrack === track) agentAudioTrack = null;
      }
      notifyChange();
    };

    const handleDataReceived = (payload, _participant, _kind, topic) => {

      if (topic === "status") {
        try {
          const text = new TextDecoder().decode(payload);
          const data = JSON.parse(text);
          if (data.status) {

            agentStatus = data.status;
            notifyChange();
          }
        } catch {
          // ignore parse errors
        }
        return;
      }

      try {
        const text = new TextDecoder().decode(payload);
        const msg = JSON.parse(text);

        if (msg.type === "status" && msg.status) {

          agentStatus = msg.status;
          notifyChange();
          return;
        }

        if (msg.type === "suggestions" && Array.isArray(msg.items)) {

          suggestions = msg.items;
          notifyChange();
          return;
        }

        if (msg.type === "slide" && typeof msg.position === "number") {

          currentSlide = msg.position;
          currentSlideMediaType = msg.media_type || null;
          currentSlideMediaUrl = msg.media_url || null;
          notifyChange();
        }
        if (msg.type === "message" && msg.content) {

          if (msg.role !== "user") {
            agentStatus = "idle";
          }
          // Skip the first backend echo of the initial message (already in transcript)
          const isDuplicate = msg.role === "user" && initialMessageEchoPending &&
            msg.content === initialMessage;
          if (isDuplicate) {

            initialMessageEchoPending = false;
          }
          if (!isDuplicate) {
            // Clear stale suggestions when user sends a new message
            if (msg.role === "user") {

              suggestions = [];
            }
            transcriptMessages = transcriptMessages.concat({
              id: `msg-${msg.timestamp || Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: msg.role === "user" ? "user" : "assistant",
              content: msg.content,
              timestamp: msg.timestamp || Date.now(),
            });
          }
          notifyChange();
        }
      } catch {
        // ignore non-JSON data messages
      }
    };

    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
    room.on(RoomEvent.DataReceived, handleDataReceived);

    room
      .connect(urlVal, tokenVal, { autoSubscribe: true })
      .then(async () => {
        // Enable mic briefly so the backend receives TrackSubscribed (signals
        // browserConnected). Data packets sent right after connect() are unreliable
        // because the WebRTC data channel may not be fully established yet.
        // Enable mic briefly so the backend receives TrackSubscribed, then disable it.
        await room.localParticipant.setMicrophoneEnabled(true);

        // Now the data channel is ready — send initial message if any.
        if (initialMessage) {
          const data = new TextEncoder().encode(JSON.stringify({ text: initialMessage }));
          await room.localParticipant.publishData(data, { reliable: true, topic: "initial-message" });
          // Show the user's message in the transcript immediately
          transcriptMessages = transcriptMessages.concat({
            id: `msg-init-${Date.now()}`,
            role: "user",
            content: initialMessage,
            timestamp: Date.now(),
          });
          agentStatus = "thinking";
          notifyChange();
        }

        // Disable mic — UI starts muted, audio capture deferred to first unmute.
        await room.localParticipant.setMicrophoneEnabled(false);
        isMicOn = false;
        isMicOnLocal = false;
        notifyChange();
      })
      .catch((err) => {
        console.error("LiveKit connection failed:", err);
      });
  }

  function disconnectRoom() {
    cleanupAudioCapture();
    if (room) {
      room.disconnect();
      room = null;
    }
    agentVideoTrack = null;
    agentAudioTrack = null;
    currentSlide = 0;
    currentSlideMediaType = null;
    currentSlideMediaUrl = null;
    transcriptMessages = [];
    notifyChange();
  }

  let audioCaptureReady = false;

  async function toggleMic() {
    if (!room) return;
    const newState = !isMicOn;
    await room.localParticipant.setMicrophoneEnabled(newState);
    isMicOn = newState;
    isMicOnLocal = newState;
    notifyChange();
    if (newState) {
      if (!audioCaptureReady) {
        audioCaptureReady = true;
        setupAudioCapture(room);
      } else {
        reconnectAudioSource(room);
      }
    }
  }

  function muteAgentAudio(mute) {
    document.querySelectorAll("#digital-employee-widget audio").forEach((el) => {
      el.muted = mute;
    });
  }

  async function pauseCall() {
    if (!room) return;
    isPaused = true;
    await room.localParticipant.setMicrophoneEnabled(false);
    isMicOnLocal = false;
    muteAgentAudio(true);
    notifyChange();
  }

  async function resumeCall() {
    if (!room) return;
    isPaused = false;
    await room.localParticipant.setMicrophoneEnabled(isMicOn);
    isMicOnLocal = isMicOn;
    if (isMicOn && audioCaptureReady) {
      reconnectAudioSource(room);
    }
    muteAgentAudio(false);
    notifyChange();
  }

  function sendTextMessage(text) {
    if (!room) return;
    const data = new TextEncoder().encode(JSON.stringify({ text }));
    room.localParticipant.publishData(data, { reliable: true, topic: "chat-text" });
  }

  // Connect immediately
  if (url && token) {
    connect(url, token);
  }

  return {
    getState,
    toggleMic,
    pauseCall,
    resumeCall,
    disconnectRoom,
    sendTextMessage,
  };
}
